import { useTimeZone } from '@/auth/useTimeZone';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormTextArea } from '@/components/forms/form-text-area';
import { useToast } from '@/components/ui/hooks/use-toast';
import { invalidateCoachPortalSessionsQueries, requestSubstitutionAsCoach } from '@/lib/api-calls';
import { cn } from '@/lib/tailwind-utils';
import { formatSubRequestDateTime } from '@airreading/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

const requestReasonMaxLength = 150;
const requestSubstituteConfirmDialogSchema = z.object({
  requestReason: z.string().max(requestReasonMaxLength, 'Reason must be 150 characters or fewer.'),
});

type RequestSubstituteConfirmDialogForm = z.infer<typeof requestSubstituteConfirmDialogSchema>;

interface RequestSubstituteConfirmDialogProps {
  sessionId: string;
  startInstant: Temporal.Instant;
  endInstant: Temporal.Instant;
  trigger: ReactNode;
  disabled?: boolean;
}

export function RequestSubstituteConfirmDialog({
  sessionId,
  startInstant,
  endInstant,
  trigger,
  disabled,
}: RequestSubstituteConfirmDialogProps) {
  const { toast } = useToast();
  const { timeZone } = useTimeZone();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: requestSubstitutionAsCoach,
  });

  const { sessionDateFormatted, sessionTimeFormatted } = useMemo(
    () => formatSubRequestDateTime(startInstant, endInstant, timeZone),
    [startInstant, endInstant, timeZone],
  );

  const sessionDateTime = `${sessionDateFormatted}, ${sessionTimeFormatted}`;

  const onSubmit = async ({ formData, closeModal, reset }: SubmitProps<RequestSubstituteConfirmDialogForm>) => {
    try {
      const trimmedRequestReason = formData.requestReason.trim();
      await mutateAsync({
        sessionId,
        requestReason: trimmedRequestReason || undefined,
      });

      toast({
        title: `Your sub request has been submitted. The session on ${sessionDateFormatted}, ${sessionTimeFormatted} has been removed from your calendar.`,
      });
      closeModal();
      reset();
      invalidateCoachPortalSessionsQueries(queryClient);
    } catch (e) {
      console.log(e);
    }
  };

  if (disabled) {
    return <>{trigger}</>;
  }

  return (
    <FormModal
      modalTitle="Request a substitute"
      onSubmit={onSubmit}
      formSchema={requestSubstituteConfirmDialogSchema}
      trigger={trigger}
      confirmBtnLabel="Request"
      defaultValues={{
        requestReason: '',
      }}
      dialogContentProps={{ className: 'lg:max-w-lg' }}
      validateOnOpen
    >
      {({ form }) => {
        const requestReasonLength = form.watch('requestReason').length;
        const isOverRequestReasonLimit = requestReasonLength > requestReasonMaxLength;

        return (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Submit a substitute request for {sessionDateTime}? This will remove it from your calendar.
            </p>
            <FormTextArea
              form={form}
              name="requestReason"
              label="Reason"
              labelEndContent={
                <span className={cn('text-sm text-muted-foreground', isOverRequestReasonLimit && 'text-destructive')}>
                  {requestReasonLength} / {requestReasonMaxLength}
                </span>
              }
              textAreaProps={{
                placeholder: 'Add a reason for requesting a substitute (optional)',
              }}
            />
          </div>
        );
      }}
    </FormModal>
  );
}
